import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ErrorToast } from "../../toast/Toasts";
import { getGiftCodeLedger } from "./services/wallet.services";
import { formatIndianNumber } from "../../_helpers/formatUtils";
import moment from "moment";
import ExpandableText from "../../components/explandabletext/ExpandableText";
import { clearGiftCodeLedgerDetails } from "./walletSlice";
import Loading from "../../components/spinner/Loading";

const GiftCodeLedger: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.wallet.loading);
  const GiftCodeLedgerDetails = useAppSelector(
    (state) => state.wallet.GiftCodeLedgerDetails
  );

  const UserID = localStorage.getItem("user_id");

  useEffect(() => {
    return () => {
      dispatch(clearGiftCodeLedgerDetails());
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      giftCode: "",
    },
    validationSchema: Yup.object({
      giftCode: Yup.string()
        .required("Gift code is required")
        .max(12, "Gift code should be maxiumn of 12 characters"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          UserID: UserID ? +UserID : null,
          GiftCode: values?.giftCode,
        };
        await dispatch(getGiftCodeLedger(payload));
      } catch (error) {
        console.error("Operation failed:", error);
        ErrorToast("An error occurred. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const formatDateTime = (dateString: string) => {
    return dateString
      ? moment(dateString, "DD-MM-YYYY HH:mm").format("DD MMM YYYY hh:mm A")
      : "-";
  };

  return (
    <>
      <div className="giftcode-ledger-page">
        <div className="container">
          <div className="row ledger-card">
            <div className="col-12">
              <h2 className="d-flex align-items-center gap-2 mb-3">
                Giftcode Ledger
              </h2>
            </div>
            <div className="col-12 mt-2">
              <form onSubmit={formik.handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="field">
                      <label
                        className={
                          formik.touched.giftCode && formik.errors.giftCode
                            ? "error-label fs-6"
                            : "fs-6"
                        }
                      >
                        Gift Code <code>*</code>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          name="giftCode"
                          className={`form-control ${
                            formik.touched.giftCode && formik.errors.giftCode
                              ? "error-border"
                              : ""
                          }`}
                          placeholder="Enter gift code"
                          value={formik.values.giftCode}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <button
                          className="fill-btn manage-btn"
                          style={{ borderRadius: "0 5px 5px 0" }}
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Checking..." : "View"}
                        </button>
                      </div>
                      {formik.touched.giftCode && formik.errors.giftCode && (
                        <div className="error-label">
                          {formik.errors.giftCode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          {GiftCodeLedgerDetails && GiftCodeLedgerDetails?.MobileNo && (
            <div className="row ledger-card mt-3 position-relative">
              <Loading loading={loading} />
              <div className="col-6">
                <div className="d-flex gap-2">
                  <h4 className="">
                    Giftcode Amount{" : "}
                    <span
                      style={{
                        color: "#00a34d",
                      }}
                    >
                      ₹{" "}
                      {formatIndianNumber(
                        GiftCodeLedgerDetails?.FlightTicketAmt || 0
                      )}
                    </span>
                  </h4>
                </div>
              </div>
              <div className="col-6">
                <div className="d-flex gap-2">
                  <h4 className="">
                    Pending Amount{" : "}
                    <span
                      style={{
                        color: "#00a34d",
                      }}
                    >
                      ₹{" "}
                      {formatIndianNumber(
                        GiftCodeLedgerDetails?.PendingAmt || 0
                      )}
                    </span>
                  </h4>
                </div>
              </div>
              <div className="col-12">
                <div className="d-flex gap-2">
                  <h4>
                    Validity{" : "}
                    <span
                      style={{
                        color: "#00a34d",
                      }}
                    >
                      {GiftCodeLedgerDetails?.FromDate}
                    </span>{" "}
                    {GiftCodeLedgerDetails?.FromDate &&
                      GiftCodeLedgerDetails?.ToDate &&
                      "to"}{" "}
                    <span
                      style={{
                        color: "#00a34d",
                      }}
                    >
                      {GiftCodeLedgerDetails?.ToDate}
                    </span>
                  </h4>
                </div>
              </div>
              <div className="col-12 mt-2">
                <div style={{ borderTop: "2px dashed gray" }}></div>
                <div className="table-responsive">
                  <table className="table align-items-center mb-0">
                    <thead
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #dee2e6",
                      }}
                    >
                      <tr>
                        <th
                          className="text-uppercase text-dark text-xs font-weight-bolder py-3"
                          style={{ width: "16%", minWidth: "100px" }}
                        >
                          Date & Time
                        </th>
                        <th
                          className="text-uppercase text-dark text-xs font-weight-bolder py-3"
                          style={{ width: "10%", minWidth: "105px" }}
                        >
                          Booking No.
                        </th>
                        <th
                          className="text-uppercase text-dark text-xs font-weight-bolder py-3"
                          style={{ width: "14%", minWidth: "100px" }}
                        >
                          Used Amount (₹)
                        </th>
                        <th
                          className="text-uppercase text-dark text-xs font-weight-bolder py-3"
                          style={{ width: "14%", minWidth: "100px" }}
                        >
                          Ticket Amount (₹)
                        </th>
                        <th
                          className="text-uppercase text-dark text-xs font-weight-bolder py-3"
                          style={{ width: "16%", minWidth: "150px" }}
                        >
                          Used By
                        </th>
                        <th
                          className="text-uppercase text-dark text-xs font-weight-bolder py-3"
                          style={{ width: "28%", minWidth: "200px" }}
                        >
                          Remarks
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {GiftCodeLedgerDetails?.GiftCodeLedgerDetail?.length >
                      0 ? (
                        GiftCodeLedgerDetails?.GiftCodeLedgerDetail?.map(
                          (transction: any) => (
                            <tr key={transction.ID}>
                              <td className="align-middle">
                                <p className="text-xs mb-0">
                                  {formatDateTime(transction.CreatedOn)}
                                </p>
                              </td>
                              <td className="align-middle">
                                <p className="text-xs mb-0 fw-bold">
                                  {transction.BookingNo}
                                </p>
                              </td>
                              <td className="align-middle">
                                <p className="text-xs  mb-0 fw-bold">
                                  ₹{" "}
                                  {formatIndianNumber(
                                    transction?.UsedAmount || 0
                                  )}
                                </p>
                              </td>
                              <td className="align-middle">
                                <p className="text-xs  mb-0 fw-bold">
                                  ₹{" "}
                                  {formatIndianNumber(
                                    transction?.BookingAmt || 0
                                  )}
                                </p>
                              </td>
                              <td className="align-middle">
                                <p className="text-xs mb-0">
                                  {transction.UsedBy || "-"}
                                </p>
                              </td>
                              <td className="align-middle">
                                <p className="text-xs mb-0">
                                  <ExpandableText text={transction.Remarks} />
                                </p>
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            <p className="text-sm text-muted mb-0">
                              No Transctions found
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GiftCodeLedger;
